import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    content: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteContentStyles;
}

export interface AutocompleteContentStyles {
    headerText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    focusedLink?: LinkPresentationProps;
    contentItem?: InjectableCss;
}

export const autocompleteContentStyles: AutocompleteContentStyles = {
    headerText: {
        weight: "bold",
        color: "secondary",
        size: 16,
        lineHeight: "22px",
        css: css`
            text-align: left;
            margin-bottom: 16px;
            display: block;
        `,
    },
    link: {
        typographyProps: {
            weight: "normal",
            size: 13,
            lineHeight: "14px",
            color: "text.main",
            css: css`
                width: 100%;
                text-align: left;
                &,
                > span {
                    color: ${getColor("text.main")} !important;

                    &:hover,
                    &:focus {
                        color: ${getColor("primary")} !important;
                    }
                }
            `,
        },
        css: css`
            width: auto;
            overflow: hidden;
        `,
    },
    contentItem: {
        css: css`
            width: 100%;
            margin-bottom: 10px;
            line-height: 1.2;
        `,
    },
};

const styles = autocompleteContentStyles;

class AutocompleteContent extends React.Component<Props> {
    private readonly styles: AutocompleteContentStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { content } = this.props;
        if (!content || content.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper
                css={css`
                    margin-bottom: 15px;
                `}
            >
                <Typography
                    {...styles.headerText}
                    data-test-selector="autocompleteContent_Header"
                    className="autocomplete-content-header"
                >
                    {translate("Content")} ({content.length})
                </Typography>
                {content.map(content => (
                    <StyledWrapper key={`${content.url}`} {...styles.contentItem}>
                        <Link
                            {...styles.link}
                            key={content.url}
                            onClick={() => {
                                this.props.goToUrl(content.url);
                            }}
                            data-test-selector="autocompleteContent_Result"
                            className="autocomplete-content-link"
                        >
                            {content.displayTitle}
                        </Link>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }
}

export default AutocompleteContent;
